/**
 * PORTED — RO-3-00c / #44470.
 * Origin: `excellent/app/_modes/lane-page.tsx`.
 *
 * Changes:
 *   loadModeData / listGoals / laneLoops / attachableLoops
 *     → `useApi(modes.lane)` (READ)
 *   next/link → RouterLink; redirect → useNavigate
 *   LaneAutomations → local port (pulse.laneAttach/Detach)
 *   LaneProposalBar left as a light notice (mutations are on pulse graph)
 */

import {
  ModeShell,
  PageRailMount,
  Button,
  SectionTitle,
  GoalsLedger,
  CommitmentLedger,
  buildFinishLine,
  parseMilestones,
  type GoalLedgerItem,
  type RailOption,
  type ModeShellView,
  type CommitmentTableRow,
} from "@excellent/ui";
import { Boxes, Plus, ArrowRight } from "@excellent/ui/icons";
import { RouterLink } from "@excellent/ui/router";
import { useApi } from "@excellent/ui/api";
import { modes } from "@excellent/ui/api/contracts/modes";
import { FocusOnHash } from "./focus-on-hash";
import { ModePlay } from "./mode-play";
import { LaneAutomations } from "./lane-automations";
import {
  type Domain,
  getDomainView,
  buildDomainViewProps,
} from "@/modes/domains";
import { modePath, type ModeSlug } from "@/modes/modes-registry";
import { DOMAIN_MOUNTS, mountSelfPads } from "./domain-mounts";

const OBJECTS_PREVIEW = 8;
const NATIVE_SUBS = new Set(["objects", "goals", "automations"]);

export function LanePage({
  mode,
  lane,
  laneUid,
  laneStatus,
  laneDescription: _laneDescription,
  underlying,
  sub,
  projectSlug,
  searchParams,
  subNav,
  workspaceId: _workspaceId,
  pulse,
}: {
  mode: ModeSlug;
  lane: Domain;
  laneUid: string;
  laneStatus?: string;
  laneDescription?: string;
  underlying: Domain | null;
  sub?: string;
  projectSlug: string | null;
  searchParams?: Record<string, string | string[] | undefined>;
  subNav: ModeShellView[];
  workspaceId: number;
  pulse?: React.ReactNode;
}) {
  const { data } = useApi(modes.lane, { query: { mode, slug: lane.slug } });

  const laneHref = modePath(mode, { projectSlug, extra: { area: lane.slug } });
  const lanePathname = modePath(mode, { projectSlug });

  const laneRows = (data?.objects ?? []) as CommitmentTableRow[];
  const bound = data?.bound ?? [];
  const attachable = data?.attachable ?? [];

  const foldedViews = (underlying?.views ?? []).filter((v) => v.component && !v.route);
  const underlyingTabs = foldedViews.map((v) => ({
    href: `${laneHref}&sub=${v.slug}`,
    label: v.label,
  }));
  const railOptions: RailOption[] = [
    { value: "overview", label: "Overview", href: laneHref },
    ...foldedViews.map((v) => ({
      value: v.slug,
      label: v.label,
      href: `${laneHref}&sub=${v.slug}`,
    })),
  ];

  // Folded domain sub-page
  const underlyingDv =
    sub && underlying && !NATIVE_SUBS.has(sub) ? getDomainView(underlying, sub) : null;
  const Mount = underlyingDv?.component
    ? DOMAIN_MOUNTS[mode]?.[underlyingDv.component]
    : undefined;
  if (underlyingDv && Mount && underlying) {
    const props = buildDomainViewProps(underlying, underlyingDv, projectSlug, sub, searchParams);
    const selfPads = mountSelfPads(underlyingDv.component);
    return (
      <ModeShell
        mode={lane.label}
        modeSlug={mode}
        modeHref={lanePathname}
        views={subNav}
        gutter={!selfPads}
        pulse={pulse}
        playStrip={<ModePlay hideWhenEmpty limit={3} mode={mode} kinds={lane.objectKinds} />}
      >
        <PageRailMount
          groupLabel={lane.label}
          representation="segmented"
          spec={{ label: "View", value: sub ?? "overview", options: railOptions }}
        />
        <Mount {...props} />
      </ModeShell>
    );
  }

  const goalsBase = projectSlug ? `/projects/${projectSlug}/operations` : "/";
  const goalsHrefBase = goalsBase.replace(/\/$/, "");
  const ledgerGoals: GoalLedgerItem[] = (data?.goals ?? []).map((g) => ({
    id: g.id,
    uid: g.uid,
    kind: g.kind,
    title: g.title,
    outcome: g.outcome,
    status: g.status as GoalLedgerItem["status"],
    task_count: g.task_count,
    done_task_count: g.done_task_count,
    cancelled_task_count: g.cancelled_task_count,
    finishLine: buildFinishLine({
      outcome: g.outcome,
      why: null,
      metric_label: g.metric_label ?? null,
      metric_baseline: g.metric_baseline ?? null,
      metric_current: g.metric_current ?? null,
      metric_target: g.metric_target ?? null,
      metric_pct: g.metric_pct ?? null,
      criteria: g.criteria_json ?? null,
    }),
    milestones: parseMilestones(g.milestones ?? null),
    lane: null,
    role: g.role,
  }));

  return (
    <ModeShell
      mode={lane.label}
      modeSlug={mode}
      modeHref={lanePathname}
      views={subNav}
      pulse={pulse}
      playStrip={<ModePlay hideWhenEmpty limit={3} mode={mode} kinds={lane.objectKinds} />}
    >
      {underlyingTabs.length ? (
        <PageRailMount
          groupLabel={lane.label}
          representation="segmented"
          spec={{ label: "View", value: "overview", options: railOptions }}
        />
      ) : null}
      {laneStatus === "proposed" ? (
        <p className="mb-3 type-body text-muted-foreground">
          This lane is proposed — accept or dismiss it from the Pulse wall.
        </p>
      ) : null}

      <div className="flex flex-col gap-10">
        <LaneSection
          title="Goals"
          count={ledgerGoals.length}
          subtitle="The outcomes steering this stage"
        >
          <GoalsLedger
            goals={ledgerGoals}
            basePath={goalsHrefBase}
            toolbar={
              <Button asChild variant="outline" size="icon-tb">
                <RouterLink
                  href={`${goalsHrefBase}/goals/new?lane=${encodeURIComponent(laneUid)}`}
                  aria-label="New goal for this lane"
                  title="New goal for this lane"
                >
                  <Plus className="size-4" />
                </RouterLink>
              </Button>
            }
          />
        </LaneSection>

        <LaneSection
          title="Automations"
          count={bound.length}
          subtitle="Workflows bound to this stage"
        >
          <LaneAutomations
            laneUid={laneUid}
            laneLabel={lane.label}
            lanePath={lanePathname}
            bound={bound}
            attachable={attachable}
          />
        </LaneSection>

        <LaneSection
          title="Objects"
          count={laneRows.length}
          subtitle="Every commitment in this stage — task, PRD, release"
          action={
            laneRows.length > OBJECTS_PREVIEW ? (
              <Button asChild variant="ghost" size="sm">
                <RouterLink href={modePath(mode, { projectSlug, view: "objects" })}>
                  Open full inventory <ArrowRight className="size-3.5" />
                </RouterLink>
              </Button>
            ) : undefined
          }
        >
          <FocusOnHash />
          <CommitmentLedger
            rows={laneRows}
            from={mode}
            limit={OBJECTS_PREVIEW}
            emptyState={{
              icon: <Boxes className="size-6" />,
              title: "Nothing here yet",
              description: `Objects show up here as work moves through ${lane.label}. Head to the Queue to pick up what to do next.`,
              action: (
                <Button asChild>
                  <RouterLink href={modePath(mode, { projectSlug, view: "queue" })}>
                    Go to Queue
                  </RouterLink>
                </Button>
              ),
            }}
          />
        </LaneSection>
      </div>
    </ModeShell>
  );
}

function LaneSection({
  title,
  count,
  subtitle,
  action,
  children,
}: {
  title: string;
  count?: number;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-3">
      <SectionTitle
        variant="plain"
        size="lg"
        title={title}
        count={count}
        subtitle={subtitle}
        action={action}
      />
      {children}
    </section>
  );
}
